import { doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebaseAppConfig";

export const RemoteConfigKeys = {
    LIVE_API_URL: 'live_api_url',
    STAGING_API_URL: 'staging_api_url',
    ANIMALKART_LIVE_URL: 'animalkart_live_url',
    ANIMALKART_STAGING_URL: 'animalkart_staging_url',
    NOTIFICATION_LIVE_URL: 'notification_live_url',
    ANIMALKART_API_TOKEN: 'animalkart_api_token',
    ANIMALKART_SWAGGER_USERNAME: 'animalkart_swagger_username',
    ANIMALKART_SWAGGER_PASSWORD: 'animalkart_swagger_password',
    ANIMALKART_AES_KEY: 'animalkart_aes_response_key',
    LIVE_USERNAME: 'live_username',
    LIVE_PASSWORD: 'live_password',
    STAGING_USERNAME: 'staging_username',
    STAGING_PASSWORD: 'staging_password',
    WHATSAPP_API_URL: 'whatsapp_api_url',
    WHATSAPP_API_TOKEN: 'whatsapp_api_token',
};

// Keys that must never be written to browser storage (tokens, passwords, AES keys)
const SENSITIVE_KEYS = new Set([
    'animalkart_api_token',
    'animalkart_swagger_username',
    'animalkart_swagger_password',
    'animalkart_aes_response_key',
    'live_username',
    'live_password',
    'staging_username',
    'staging_password',
    'whatsapp_api_token',
]);

const SESSION_STORAGE_KEY = 'farmvest_remote_config';

class RemoteConfigService {
    private static instance: RemoteConfigService;
    private config: Record<string, string> = {};

    private constructor() { }

    static getInstance(): RemoteConfigService {
        if (!RemoteConfigService.instance) {
            RemoteConfigService.instance = new RemoteConfigService();
        }
        return RemoteConfigService.instance;
    }

    async fetchConfig(): Promise<void> {
        try {
            const docRef = doc(db, "app_settings", "farmvest_config");
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                this.config = docSnap.data() as Record<string, string>;
                this.persistNonSensitive();
            } else {
                console.warn("No such document in app_settings/farmvest_config!");
            }
        } catch (error) {
            console.error("Error fetching remote config:", error);
        }
    }

    /** Persist only non-sensitive keys to sessionStorage (cleared on tab close). */
    private persistNonSensitive(): void {
        const safe: Record<string, string> = {};
        for (const [key, value] of Object.entries(this.config)) {
            if (!SENSITIVE_KEYS.has(key)) {
                safe[key] = value;
            }
        }
        try {
            sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(safe));
        } catch (_) { }
    }

    getValue(key: string, defaultValue: string | null = null): string | null {
        // 1. In-memory config (fetched this session from Firestore)
        if (this.config[key] !== undefined) {
            return this.config[key];
        }

        // 2. sessionStorage fallback — only for non-sensitive keys
        if (!SENSITIVE_KEYS.has(key)) {
            const saved = sessionStorage.getItem(SESSION_STORAGE_KEY);
            if (saved) {
                try {
                    const parsed: Record<string, string> = JSON.parse(saved);
                    if (parsed[key] !== undefined) return parsed[key];
                } catch (_) { }
            }
        }

        return defaultValue;
    }

    getAllConfig(): Record<string, string> {
        return { ...this.config };
    }
}

export const remoteConfig = RemoteConfigService.getInstance();
